/**
 * Test Configuration Manager
 * Handles persistent storage of test user configurations and settings
 */

import { TEST_USER_PROFILES, OPPORTUNITY_TEMPLATES, MESSAGE_TYPES } from '../models/BusinessMessageModels';
import RealUserService from '../services/RealUserService';

const STORAGE_KEYS = {
  TEST_CONFIG: 'universalMessaging_testConfig',
  ACTIVE_USER: 'universalMessaging_activeUser',
  CONVERSATION_HISTORY: 'universalMessaging_conversationHistory',
  MESSAGE_TEMPLATES: 'universalMessaging_messageTemplates'
};

export class TestConfigManager {
  constructor() {
    this.initializeDefaults();
  }

  // Initialize default configuration if none exists
  initializeDefaults() {
    const existingConfig = this.getTestConfig();
    if (!existingConfig || Object.keys(existingConfig).length === 0) {
      this.saveTestConfig({
        userProfiles: TEST_USER_PROFILES,
        opportunities: OPPORTUNITY_TEMPLATES,
        activeConversations: [],
        messageHistory: [],
        settings: {
          autoFillEnabled: true,
          persistHistory: true,
          showRateCalculations: true,
          simulateDelay: false
        }
      });
    }
  }

  // Get full test configuration
  getTestConfig() {
    try {
      const config = localStorage.getItem(STORAGE_KEYS.TEST_CONFIG);
      return config ? JSON.parse(config) : {};
    } catch (error) {
      console.error('Error loading test configuration:', error);
      return {};
    }
  }

  // Save test configuration
  saveTestConfig(config) {
    try {
      localStorage.setItem(STORAGE_KEYS.TEST_CONFIG, JSON.stringify(config));
      return true;
    } catch (error) {
      console.error('Error saving test configuration:', error);
      return false;
    }
  }

  // Get user profiles (with real user integration and categorization)
  async getUserProfiles(useRealUsers = false, currentUser = null) {
    if (useRealUsers) {
      try {
        console.log('[TestConfigManager] Attempting to fetch real user data...');
        const realData = await RealUserService.getAllRealData(currentUser);

        if (realData && realData.users) {
          // Check if we got categorized users (new format) or flat users (old format)
          if (realData.users.all && typeof realData.users.all === 'object') {
            console.log(`[TestConfigManager] Successfully loaded categorized real users`);
            console.log(`- Voice Actors: ${Object.keys(realData.users.voiceActors || {}).length}`);
            console.log(`- Book Creators: ${Object.keys(realData.users.bookCreators || {}).length}`);
            console.log(`- Admins: ${Object.keys(realData.users.admins || {}).length}`);
            return realData.users;
          } else if (Object.keys(realData.users).length > 0) {
            console.log(`[TestConfigManager] Successfully loaded ${Object.keys(realData.users).length} real users (flat format)`);
            // Return flat format for backward compatibility
            return realData.users;
          } else {
            console.warn('[TestConfigManager] No real users returned from service');
            throw new Error('No real users available from the database');
          }
        } else {
          console.warn('[TestConfigManager] No real users returned from service');
          throw new Error('No real users available from the database');
        }
      } catch (error) {
        console.warn('[TestConfigManager] Failed to fetch real users, falling back to mock data:', error);
        // Re-throw the error so the calling component can handle it appropriately
        throw new Error(`Real user data unavailable: ${error.message}`);
      }
    }

    // Return mock data for non-real mode - convert to categorized format
    const config = this.getTestConfig();
    const mockUsers = config.userProfiles || TEST_USER_PROFILES;
    return this.categorizeMockUsers(mockUsers);
  }

  // Categorize mock users into the new format
  categorizeMockUsers(mockUsers) {
    const categorizedUsers = {
      voiceActors: {},
      bookCreators: {},
      admins: {},
      all: {}
    };

    Object.entries(mockUsers).forEach(([key, user]) => {
      // Add profile relationship flags for mock users
      const enhancedUser = {
        ...user,
        hasVoiceActorProfile: user.role === 'VOICE_ACTOR',
        hasBookCreatorProfile: user.role === 'BOOK_CREATOR',
        hasAdminProfile: user.role === 'ADMIN',
        isRealUser: false
      };

      // Categorize based on role
      if (enhancedUser.role === 'VOICE_ACTOR') {
        categorizedUsers.voiceActors[key] = enhancedUser;
      }
      if (enhancedUser.role === 'BOOK_CREATOR') {
        categorizedUsers.bookCreators[key] = enhancedUser;
      }
      if (enhancedUser.role === 'ADMIN') {
        categorizedUsers.admins[key] = enhancedUser;
      }

      // Add to all users
      categorizedUsers.all[key] = enhancedUser;
    });

    return categorizedUsers;
  }

  // Get users by category
  async getUsersByCategory(category, useRealUsers = false, currentUser = null) {
    const allUsers = await this.getUserProfiles(useRealUsers, currentUser);

    // Handle both categorized and flat formats
    if (allUsers.all && typeof allUsers.all === 'object') {
      // New categorized format
      switch (category) {
        case 'voiceActors':
          return allUsers.voiceActors || {};
        case 'bookCreators':
          return allUsers.bookCreators || {};
        case 'admins':
          return allUsers.admins || {};
        case 'all':
        default:
          return allUsers.all || {};
      }
    } else {
      // Flat format - filter by role
      const filteredUsers = {};
      Object.entries(allUsers).forEach(([key, user]) => {
        let shouldInclude = false;
        switch (category) {
          case 'voiceActors':
            shouldInclude = user.role === 'VOICE_ACTOR';
            break;
          case 'bookCreators':
            shouldInclude = user.role === 'BOOK_CREATOR';
            break;
          case 'admins':
            shouldInclude = user.role === 'ADMIN';
            break;
          case 'all':
          default:
            shouldInclude = true;
            break;
        }
        if (shouldInclude) {
          filteredUsers[key] = user;
        }
      });
      return filteredUsers;
    }
  }

  // Get user profiles synchronously (for backward compatibility)
  getUserProfilesSync() {
    const config = this.getTestConfig();
    return config.userProfiles || TEST_USER_PROFILES;
  }

  // Add or update user profile
  saveUserProfile(profileId, profile) {
    const config = this.getTestConfig();
    config.userProfiles = config.userProfiles || {};
    config.userProfiles[profileId] = {
      ...profile,
      lastUpdated: new Date().toISOString()
    };
    return this.saveTestConfig(config);
  }

  // Get active user
  getActiveUser() {
    try {
      const activeUserId = localStorage.getItem(STORAGE_KEYS.ACTIVE_USER);
      if (activeUserId) {
        const profiles = this.getUserProfiles();
        return profiles[activeUserId] || null;
      }
      return null;
    } catch (error) {
      console.error('Error loading active user:', error);
      return null;
    }
  }

  // Set active user
  setActiveUser(userId) {
    try {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_USER, userId);
      return true;
    } catch (error) {
      console.error('Error setting active user:', error);
      return false;
    }
  }

  // Get conversation participants based on message type and active user
  getConversationParticipants(messageType) {
    const activeUser = this.getActiveUser();
    const profiles = this.getUserProfiles();

    if (!activeUser) return [];

    const participants = [];

    // Add appropriate recipients based on message type and user role
    switch (messageType) {
      case 'VOICE_ACTOR_APPLICATION':
        if (activeUser.role === 'VOICE_ACTOR') {
          // Voice actors send to book creators
          Object.values(profiles).forEach(profile => {
            if (profile.role === 'BOOK_CREATOR') {
              participants.push({
                userId: profile.userId,
                name: profile.name,
                role: profile.role,
                suggested: true
              });
            }
          });
        }
        break;

      case 'APPLICATION_STATUS_UPDATE':
        if (activeUser.role === 'BOOK_CREATOR') {
          // Book creators send to voice actors
          Object.values(profiles).forEach(profile => {
            if (profile.role === 'VOICE_ACTOR') {
              participants.push({
                userId: profile.userId,
                name: profile.name,
                role: profile.role,
                suggested: true
              });
            }
          });
        }
        break;

      case 'FINANCIAL_TRANSACTION':
        // Both roles can send financial messages to each other
        Object.values(profiles).forEach(profile => {
          if (profile.role !== activeUser.role &&
            (profile.role === 'VOICE_ACTOR' || profile.role === 'BOOK_CREATOR')) {
            participants.push({
              userId: profile.userId,
              name: profile.name,
              role: profile.role,
              suggested: true
            });
          }
        });
        break;

      case 'PROJECT_MILESTONE':
        // Project updates go to all project participants
        Object.values(profiles).forEach(profile => {
          if (profile.role === 'VOICE_ACTOR' || profile.role === 'BOOK_CREATOR') {
            participants.push({
              userId: profile.userId,
              name: profile.name,
              role: profile.role,
              suggested: profile.role !== activeUser.role
            });
          }
        });
        break;

      case 'HELP_DESK':
        if (activeUser.role !== 'ADMIN') {
          // Non-admins send help desk messages to admins
          Object.values(profiles).forEach(profile => {
            if (profile.role === 'ADMIN') {
              participants.push({
                userId: profile.userId,
                name: profile.name,
                role: profile.role,
                suggested: true
              });
            }
          });
        }
        break;

      case 'SYSTEM_NOTIFICATION':
        if (activeUser.role === 'ADMIN') {
          // Admins send system notifications to all users
          Object.values(profiles).forEach(profile => {
            if (profile.role !== 'ADMIN') {
              participants.push({
                userId: profile.userId,
                name: profile.name,
                role: profile.role,
                suggested: true
              });
            }
          });
        }
        break;

      default:
        // For unknown message types, show all users except self
        Object.values(profiles).forEach(profile => {
          if (profile.userId !== activeUser.userId) {
            participants.push({
              userId: profile.userId,
              name: profile.name,
              role: profile.role,
              suggested: false
            });
          }
        });
    }

    return participants;
  }

  // Get conversation history
  getConversationHistory() {
    try {
      const history = localStorage.getItem(STORAGE_KEYS.CONVERSATION_HISTORY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error loading conversation history:', error);
      return [];
    }
  }

  // Add message to conversation history
  addToConversationHistory(message) {
    try {
      const history = this.getConversationHistory();
      const newMessage = {
        ...message,
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        testMessage: true
      };

      history.unshift(newMessage); // Add to beginning

      // Keep only last 100 messages
      const trimmedHistory = history.slice(0, 100);

      localStorage.setItem(STORAGE_KEYS.CONVERSATION_HISTORY, JSON.stringify(trimmedHistory));
      return newMessage;
    } catch (error) {
      console.error('Error adding to conversation history:', error);
      return null;
    }
  }

  // Clear conversation history
  clearConversationHistory() {
    try {
      localStorage.removeItem(STORAGE_KEYS.CONVERSATION_HISTORY);
      return true;
    } catch (error) {
      console.error('Error clearing conversation history:', error);
      return false;
    }
  }

  // Get opportunities (with real opportunity integration)
  async getOpportunities(useRealOpportunities = false) {
    if (useRealOpportunities) {
      try {
        const realData = await RealUserService.getAllRealData();
        if (realData.opportunities) {
          // Merge real opportunities with mock opportunities
          const mockOpportunities = this.getTestConfig().opportunities || OPPORTUNITY_TEMPLATES;
          return {
            ...mockOpportunities,
            ...realData.opportunities
          };
        }
      } catch (error) {
        console.warn('Failed to fetch real opportunities, falling back to mock data:', error);
      }
    }

    const config = this.getTestConfig();
    return config.opportunities || OPPORTUNITY_TEMPLATES;
  }

  // Get opportunities synchronously (for backward compatibility)
  getOpportunitiesSync() {
    const config = this.getTestConfig();
    return config.opportunities || OPPORTUNITY_TEMPLATES;
  }

  // Get applications (with real application integration)
  async getApplications(useRealApplications = false) {
    if (useRealApplications) {
      try {
        const realData = await RealUserService.getAllRealData();
        if (realData.applications) {
          return realData.applications;
        }
      } catch (error) {
        console.warn('Failed to fetch real applications, falling back to empty data:', error);
      }
    }

    // Return mock applications if no real data
    return {};
  }

  // Add or update opportunity
  saveOpportunity(opportunityId, opportunity) {
    const config = this.getTestConfig();
    config.opportunities = config.opportunities || {};
    config.opportunities[opportunityId] = {
      ...opportunity,
      lastUpdated: new Date().toISOString()
    };
    return this.saveTestConfig(config);
  }

  // Get business message scenarios for testing
  getBusinessMessageScenarios() {
    return [
      {
        id: 'voice_actor_application',
        name: 'Voice Actor Application',
        description: 'Voice actor applies for a book opportunity',
        messageType: 'VOICE_ACTOR_APPLICATION',
        requiredRoles: ['VOICE_ACTOR', 'BOOK_CREATOR'],
        scenario: {
          sender: 'VOICE_ACTOR',
          recipient: 'BOOK_CREATOR',
          context: 'Application for a posted opportunity with rate proposal',
          messageType: 'VOICE_ACTOR_APPLICATION'
        }
      },
      {
        id: 'application_status_update',
        name: 'Application Status Update',
        description: 'Book creator responds to voice actor application',
        messageType: 'APPLICATION_STATUS_UPDATE',
        requiredRoles: ['BOOK_CREATOR', 'VOICE_ACTOR'],
        scenario: {
          sender: 'BOOK_CREATOR',
          recipient: 'VOICE_ACTOR',
          context: 'Response to submitted application with acceptance or negotiation',
          messageType: 'APPLICATION_STATUS_UPDATE'
        }
      },
      {
        id: 'financial_transaction',
        name: 'Financial Transaction',
        description: 'Payment notification and milestone completion',
        messageType: 'FINANCIAL_TRANSACTION',
        requiredRoles: ['VOICE_ACTOR', 'BOOK_CREATOR'],
        scenario: {
          sender: 'BOOK_CREATOR',
          recipient: 'VOICE_ACTOR',
          context: 'Milestone payment release with quality metrics',
          messageType: 'FINANCIAL_TRANSACTION'
        }
      },
      {
        id: 'project_milestone',
        name: 'Project Milestone Update',
        description: 'Progress update on ongoing project',
        messageType: 'PROJECT_MILESTONE',
        requiredRoles: ['VOICE_ACTOR', 'BOOK_CREATOR'],
        scenario: {
          sender: 'VOICE_ACTOR',
          recipient: 'BOOK_CREATOR',
          context: 'Progress report with quality metrics and timeline updates',
          messageType: 'PROJECT_MILESTONE'
        }
      },
      {
        id: 'voice_actor_application_alt',
        name: 'Voice Actor Application (Alternative)',
        description: 'Alternative voice actor application scenario',
        messageType: 'VOICE_ACTOR_APPLICATION',
        requiredRoles: ['VOICE_ACTOR', 'BOOK_CREATOR'],
        scenario: {
          sender: 'VOICE_ACTOR',
          recipient: 'BOOK_CREATOR',
          context: 'Application with different experience level and rate structure',
          messageType: 'VOICE_ACTOR_APPLICATION'
        }
      },
      {
        id: 'project_milestone_completion',
        name: 'Project Milestone Completion',
        description: 'Major milestone completion notification',
        messageType: 'PROJECT_MILESTONE',
        requiredRoles: ['VOICE_ACTOR', 'BOOK_CREATOR'],
        scenario: {
          sender: 'VOICE_ACTOR',
          recipient: 'BOOK_CREATOR',
          context: 'Quarter completion with detailed quality analysis',
          messageType: 'PROJECT_MILESTONE'
        }
      }
    ];
  }

  // Normalize role names between different formats
  normalizeRole(role) {
    if (!role) return null;

    // Convert to uppercase with underscores for consistent comparison
    const roleMap = {
      'voiceActor': 'VOICE_ACTOR',
      'voice_actor': 'VOICE_ACTOR',
      'VOICE_ACTOR': 'VOICE_ACTOR',
      'bookCreator': 'BOOK_CREATOR',
      'book_creator': 'BOOK_CREATOR',
      'BOOK_CREATOR': 'BOOK_CREATOR',
      'admin': 'ADMIN',
      'ADMIN': 'ADMIN'
    };

    return roleMap[role] || role.toUpperCase();
  }

  // Get user role from multiple possible fields
  getUserRole(user) {
    // Check multiple possible role fields and normalize them
    const possibleRoles = [
      user.role,           // Mock data format
      user.primaryRole,    // Real data user format
      user.type           // Real data profile format
    ].filter(Boolean);

    for (const role of possibleRoles) {
      const normalizedRole = this.normalizeRole(role);
      if (normalizedRole) {
        return normalizedRole;
      }
    }

    return null;
  }

  // Generate test scenario data
  async generateScenarioData(scenarioId, useRealData = false) {
    const scenarios = this.getBusinessMessageScenarios();
    const scenario = scenarios.find(s => s.id === scenarioId);

    if (!scenario) {
      throw new Error(`Scenario ${scenarioId} not found`);
    }

    const users = await this.getUserProfiles(useRealData);
    const opportunities = await this.getOpportunities(useRealData);
    const applications = await this.getApplications(useRealData);

    // Handle both categorized and flat user formats
    let allUsers = [];
    if (users.all && typeof users.all === 'object') {
      // New categorized format
      allUsers = Object.values(users.all);
    } else {
      // Flat format
      allUsers = Object.values(users);
    }

    // Find appropriate users for the scenario using normalized role matching
    const senderUsers = allUsers.filter(u => {
      const userRole = this.getUserRole(u);
      return userRole === scenario.scenario.sender;
    });

    const recipientUsers = allUsers.filter(u => {
      const userRole = this.getUserRole(u);
      return userRole === scenario.scenario.recipient;
    });

    // Enhanced error reporting with detailed diagnostics
    if (senderUsers.length === 0 || recipientUsers.length === 0) {
      // Analyze what users we have
      const roleCount = {};
      allUsers.forEach(user => {
        const userRole = this.getUserRole(user);
        roleCount[userRole] = (roleCount[userRole] || 0) + 1;
      });

      const availableRoles = Object.keys(roleCount).sort();
      const roleDetails = availableRoles.map(role =>
        `${role}: ${roleCount[role]}`
      ).join(', ');

      // Build detailed error message
      let errorDetails = [
        `Failed to run scenario '${scenario.name}': Missing required users`,
        `Required roles: ${scenario.scenario.sender} (sender) → ${scenario.scenario.recipient} (recipient)`,
        ``,
        `Current availability:`,
        `- ${scenario.scenario.sender}: ${senderUsers.length} available ${senderUsers.length >= 1 ? '✓' : '✗ (need at least 1)'}`,
        `- ${scenario.scenario.recipient}: ${recipientUsers.length} available ${recipientUsers.length >= 1 ? '✓' : '✗ (need at least 1)'}`,
        ``,
        `Total users loaded: ${allUsers.length}`,
        `Roles found: ${roleDetails || 'None'}`,
        `Data source: ${useRealData ? 'Real database' : 'Mock data'}`
      ];

      // Add user format info for debugging
      if (users.all && typeof users.all === 'object') {
        errorDetails.push(`User format: Categorized (Voice Actors: ${Object.keys(users.voiceActors || {}).length}, Book Creators: ${Object.keys(users.bookCreators || {}).length}, Admins: ${Object.keys(users.admins || {}).length})`);
      } else {
        errorDetails.push(`User format: Flat`);
      }

      throw new Error(errorDetails.join('\n'));
    }

    // Select sender and recipient, ensuring they are different users
    const sender = senderUsers[0];
    let recipient = recipientUsers[0];

    // If sender and recipient are the same user, try to find a different recipient
    if (sender.userId === recipient.userId && recipientUsers.length > 1) {
      recipient = recipientUsers[1];
    } else if (sender.userId === recipient.userId) {
      // If we only have one user of the recipient type, and it's the same as sender,
      // this indicates a data issue - log warning but proceed
      console.warn(`[TestConfigManager] Sender and recipient are the same user (${sender.name}) for scenario ${scenario.name}. This may indicate insufficient test data.`);
    }
    const opportunity = Object.values(opportunities)[0];

    return {
      scenario,
      sender,
      recipient,
      opportunity,
      applications: Object.values(applications),
      messageTemplate: await RealUserService.createBusinessMessage(
        scenario.messageType,
        sender,
        recipient,
        { opportunity }
      )
    };
  }

  // Get settings
  getSettings() {
    const config = this.getTestConfig();
    return config.settings || {
      autoFillEnabled: true,
      persistHistory: true,
      showRateCalculations: true,
      simulateDelay: false
    };
  }

  // Update settings
  updateSettings(newSettings) {
    const config = this.getTestConfig();
    config.settings = {
      ...config.settings,
      ...newSettings
    };
    return this.saveTestConfig(config);
  }

  // Export configuration
  exportConfig() {
    const config = this.getTestConfig();
    const exportData = {
      ...config,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `messaging-test-config-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Import configuration
  importConfig(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const importedConfig = JSON.parse(e.target.result);

          // Validate imported config
          if (importedConfig.userProfiles && importedConfig.opportunities) {
            this.saveTestConfig(importedConfig);
            resolve(importedConfig);
          } else {
            reject(new Error('Invalid configuration file format'));
          }
        } catch (error) {
          reject(new Error('Failed to parse configuration file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read configuration file'));
      };

      reader.readAsText(file);
    });
  }

  // Reset to defaults
  resetToDefaults() {
    try {
      // Clear all stored data
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });

      // Reinitialize with defaults
      this.initializeDefaults();
      return true;
    } catch (error) {
      console.error('Error resetting to defaults:', error);
      return false;
    }
  }

  // Get statistics
  getStatistics() {
    const history = this.getConversationHistory();
    const profiles = this.getUserProfiles();

    const stats = {
      totalMessages: history.length,
      totalProfiles: Object.keys(profiles).length,
      messagesByType: {},
      messagesByRole: {},
      recentActivity: history.slice(0, 10)
    };

    // Count messages by type and role
    history.forEach(message => {
      const messageType = message.messageType || 'UNKNOWN';
      const senderRole = message.senderRole || 'UNKNOWN';

      stats.messagesByType[messageType] = (stats.messagesByType[messageType] || 0) + 1;
      stats.messagesByRole[senderRole] = (stats.messagesByRole[senderRole] || 0) + 1;
    });

    return stats;
  }
}

// Create singleton instance
export const testConfigManager = new TestConfigManager();

export default testConfigManager;
